/**
 * Enhanced Firestore Schema - Phase 1: AI Integration Foundation
 *
 * This file defines the enhanced data schemas for Firestore collections
 * to support AI-enhanced beverage data, professional ratings, and
 * advanced features like pairing recommendations.
 */

/**
 * Enhanced Beverage Document Schema
 */
export const ENHANCED_BEVERAGE_SCHEMA = {
  // Original restaurant data (preserved)
  id: "string",
  name: "string",
  type: "string", // wine, beer, spirits, cocktails
  category: "string",
  price: "number",
  description: "string",
  available: "boolean",

  // Restaurant-specific fields
  restaurantId: "string",
  createdAt: "timestamp",
  updatedAt: "timestamp",
  createdBy: "string",

  // Legacy fields (maintained for backward compatibility)
  winery: "string",
  region: "string",
  country: "string",
  vintage: "string",
  varietal: "string",

  // New enhanced AI data
  aiEnhanced: {
    lastEnhanced: "timestamp",
    version: "string", // Schema version for migration
    confidence: "number", // 0-1 confidence score

    // Professional ratings from multiple sources
    ratings: [
      {
        source: "string", // Wine Spectator, Wine Enthusiast, etc.
        score: "number",
        scale: "number", // Usually 100
        review: "string", // Optional review excerpt
        date: "timestamp", // When rating was published
        verified: "boolean", // Whether rating is from verified source
      },
    ],

    // Enhanced tasting notes
    tastingNotes: {
      appearance: "string",
      nose: "string", // Aroma
      palate: "string", // Taste
      finish: "string",
      overall: "string", // General description

      // Structured flavor components
      flavors: ["string"], // Array of flavor descriptors
      aromas: ["string"], // Array of aroma descriptors
      characteristics: ["string"], // full-bodied, crisp, etc.
    },

    // Producer/distillery/brewery information
    producer: {
      name: "string",
      location: {
        country: "string",
        region: "string",
        subRegion: "string",
        appellation: "string", // For wines
      },
      established: "number", // Year
      history: "string",
      philosophy: "string",
      signature: "string", // What they're known for
      accolades: ["string"], // Awards and recognition

      // Contact/website info
      website: "string",
      socialMedia: {
        instagram: "string",
        facebook: "string",
        twitter: "string",
      },
    },

    // Technical production data
    technicalData: {
      // Wine-specific
      alcoholContent: "string",
      residualSugar: "string",
      pH: "number",
      totalAcidity: "string",
      tannins: "string",
      sulfites: "string",

      // Beer-specific
      abv: "string",
      ibu: "number", // International Bitterness Units
      og: "number", // Original Gravity
      fg: "number", // Final Gravity
      srm: "number", // Standard Reference Method (color)
      ebc: "number", // European Brewery Convention
      style: "string",

      // Spirits-specific
      proof: "number",
      aged: "string", // Aging information
      mashBill: "string", // For whiskeys
      distillationMethod: "string",

      // General
      servingTemperature: "string",
      cellaring: "string", // Aging potential
      vintage: "string",
      bottleSize: "string",
    },

    // Food pairing recommendations
    pairings: {
      recommended: ["string"], // General pairing suggestions
      menuItems: [
        // Specific pairings with restaurant's menu
        {
          itemName: "string",
          category: "string", // appetizer, main, dessert
          confidence: "number", // 0-1 pairing confidence
          explanation: "string", // Why they pair well
        },
      ],
      cuisineStyles: ["string"], // Italian, Asian, etc.
      occasions: ["string"], // romantic dinner, celebration, etc.
      seasons: ["string"], // summer, winter, etc.
    },

    // Market and pricing data
    marketData: {
      averagePrice: "number",
      priceRange: "string",
      availability: "string", // Limited, Widely Available, etc.
      vintage: "string",

      // Price history and trends
      priceHistory: [
        {
          price: "number",
          date: "timestamp",
          source: "string",
        },
      ],

      // Inventory suggestions
      inventoryNotes: "string",
      orderingInfo: {
        distributor: "string",
        minimumOrder: "number",
        leadTime: "string",
      },
    },

    // Expert reviews and commentary
    expertReviews: [
      {
        reviewer: "string", // Expert name
        publication: "string",
        rating: "number",
        review: "string",
        date: "timestamp",
        url: "string", // Link to full review
      },
    ],

    // AI-generated cocktail recipes (for spirits)
    cocktailRecipes: [
      {
        name: "string",
        ingredients: [
          {
            name: "string",
            amount: "string",
            unit: "string",
          },
        ],
        instructions: "string",
        glass: "string",
        garnish: "string",
        difficulty: "string", // Easy, Medium, Hard
        image: "string",
      },
    ],

    // Similar beverages recommendations
    similarities: [
      {
        beverageId: "string",
        similarityScore: "number", // 0-1
        reasonsForSimilarity: ["string"],
      },
    ],
  },

  // Enhanced media
  media: {
    primaryImage: "string",
    images: ["string"],
    videos: ["string"],

    // AI-generated or enhanced images
    aiGeneratedImages: [
      {
        url: "string",
        type: "string", // label, bottle, serving, etc.
        confidence: "number",
      },
    ],
  },
};

/**
 * Restaurant Menu Context Schema
 * For AI pairing recommendations
 */
export const MENU_CONTEXT_SCHEMA = {
  restaurantId: "string",
  lastUpdated: "timestamp",

  // Food menu categories and items
  foodMenu: [
    {
      category: "string", // appetizers, mains, desserts
      items: [
        {
          name: "string",
          description: "string",
          price: "number",

          // AI analysis of dish
          aiAnalysis: {
            cuisineStyle: "string",
            mainIngredients: ["string"],
            cookingMethod: "string",
            flavor: "string", // rich, light, spicy, etc.

            // Auto-suggested pairings
            suggestedBeverages: [
              {
                beverageId: "string",
                confidence: "number",
                explanation: "string",
              },
            ],
          },
        },
      ],
    },
  ],
};

/**
 * AI Enhancement Tracking Schema
 * For monitoring and improving AI performance
 */
export const AI_ENHANCEMENT_LOG_SCHEMA = {
  beverageId: "string",
  restaurantId: "string",
  timestamp: "timestamp",

  // Enhancement process details
  process: {
    version: "string", // AI service version
    sources: ["string"], // Which APIs/services were used
    duration: "number", // Processing time in ms
    errors: ["string"], // Any errors encountered
    confidence: "number", // Overall confidence in enhancement
  },

  // Data sources used
  dataSources: [
    {
      name: "string", // Wine.com, Vivino, AI, etc.
      success: "boolean",
      confidence: "number",
      dataPoints: ["string"], // What data was retrieved
    },
  ],

  // Quality metrics
  quality: {
    completeness: "number", // 0-1 how complete the data is
    accuracy: "number", // 0-1 estimated accuracy
    freshness: "number", // 0-1 how recent the data is
    relevance: "number", // 0-1 how relevant to restaurant's needs
  },
};

/**
 * User Preferences Schema
 * For personalized recommendations
 */
export const USER_PREFERENCES_SCHEMA = {
  userId: "string",
  restaurantId: "string", // Can have different preferences per restaurant

  preferences: {
    beverageTypes: ["string"], // wine, beer, spirits, cocktails

    // Wine preferences
    winePreferences: {
      colors: ["string"], // red, white, rosé, sparkling
      bodyTypes: ["string"], // light, medium, full
      sweetness: ["string"], // dry, off-dry, sweet
      regions: ["string"],
      priceRange: {
        min: "number",
        max: "number",
      },
    },

    // Beer preferences
    beerPreferences: {
      styles: ["string"], // IPA, Lager, Stout, etc.
      strength: ["string"], // Light, Medium, Strong
      bitterness: ["string"], // Low, Medium, High
      color: ["string"], // Light, Amber, Dark
    },

    // Spirits preferences
    spiritsPreferences: {
      types: ["string"], // whiskey, vodka, gin, etc.
      proof: ["string"], // Low, Medium, High
      aged: ["string"], // Young, Aged, Very Aged
      flavors: ["string"], // Smooth, Smoky, Spicy, etc.
    },

    // General preferences
    occasions: ["string"], // casual, formal, celebration
    seasons: ["string"], // spring, summer, fall, winter
    foodPairings: ["string"], // What foods they typically eat
  },

  // Learning from user behavior
  behavior: {
    viewedBeverages: ["string"],
    favoriteBeverages: ["string"],
    dislikedBeverages: ["string"],

    // Interaction patterns
    sessionData: [
      {
        timestamp: "timestamp",
        beverageId: "string",
        action: "string", // viewed, liked, disliked, ordered
        context: "string", // What led to this interaction
      },
    ],
  },
};

/**
 * Collection Names for Firestore
 */
export const COLLECTIONS = {
  BEVERAGES: "beverages",
  ENHANCED_BEVERAGES: "enhanced_beverages", // New collection for AI-enhanced data
  RESTAURANTS: "restaurants",
  USERS: "users",
  MENU_CONTEXTS: "menu_contexts",
  AI_ENHANCEMENT_LOGS: "ai_enhancement_logs",
  USER_PREFERENCES: "user_preferences",

  // Analytics collections
  RATINGS_CACHE: "ratings_cache", // Cache external API ratings
  PAIRING_SUGGESTIONS: "pairing_suggestions",
  RECOMMENDATION_CACHE: "recommendation_cache",
};

/**
 * Firestore Security Rules Template
 * These rules should be added to firestore.rules
 */
export const SECURITY_RULES_TEMPLATE = `
rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    
    // Enhanced beverages - restaurant owners can read/write their own
    match /enhanced_beverages/{beverageId} {
      allow read: if request.auth != null;
      allow write: if request.auth != null && 
        (request.auth.uid == resource.data.createdBy || 
         isRestaurantAdmin(resource.data.restaurantId));
    }
    
    // Menu contexts - restaurant specific
    match /menu_contexts/{restaurantId} {
      allow read: if request.auth != null;
      allow write: if request.auth != null && isRestaurantAdmin(restaurantId);
    }
    
    // AI enhancement logs - read-only for users, write for system
    match /ai_enhancement_logs/{logId} {
      allow read: if request.auth != null;
      allow write: if request.auth != null; // Should be restricted to cloud functions
    }
    
    // User preferences - user-specific
    match /user_preferences/{userId} {
      allow read, write: if request.auth != null && request.auth.uid == userId;
    }
    
    // Cache collections - read for authenticated users
    match /ratings_cache/{itemId} {
      allow read: if request.auth != null;
      allow write: if false; // Only cloud functions should write
    }
    
    match /pairing_suggestions/{itemId} {
      allow read: if request.auth != null;
      allow write: if false; // Only cloud functions should write
    }
    
    // Helper function to check restaurant admin
    function isRestaurantAdmin(restaurantId) {
      return exists(/databases/$(database)/documents/restaurants/$(restaurantId)/admins/$(request.auth.uid));
    }
  }
}
`;

/**
 * Firestore Indexes Configuration
 * These indexes should be created for optimal performance
 */
export const FIRESTORE_INDEXES = [
  {
    collectionGroup: "enhanced_beverages",
    queryScope: "COLLECTION",
    fields: [
      { fieldPath: "restaurantId", order: "ASCENDING" },
      { fieldPath: "type", order: "ASCENDING" },
      { fieldPath: "available", order: "ASCENDING" },
      { fieldPath: "aiEnhanced.lastEnhanced", order: "DESCENDING" },
    ],
  },
  {
    collectionGroup: "enhanced_beverages",
    queryScope: "COLLECTION",
    fields: [
      { fieldPath: "restaurantId", order: "ASCENDING" },
      { fieldPath: "aiEnhanced.ratings.score", order: "DESCENDING" },
    ],
  },
  {
    collectionGroup: "ai_enhancement_logs",
    queryScope: "COLLECTION",
    fields: [
      { fieldPath: "restaurantId", order: "ASCENDING" },
      { fieldPath: "timestamp", order: "DESCENDING" },
    ],
  },
];

const enhancedDataSchema = {
  ENHANCED_BEVERAGE_SCHEMA,
  MENU_CONTEXT_SCHEMA,
  AI_ENHANCEMENT_LOG_SCHEMA,
  USER_PREFERENCES_SCHEMA,
  COLLECTIONS,
  SECURITY_RULES_TEMPLATE,
  FIRESTORE_INDEXES,
};

export default enhancedDataSchema;
